import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { CreditCard, Loader2, CheckCircle2, AlertTriangle, Sparkles, Calendar } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { SubscriptionManager } from "@/components/billing/SubscriptionManager";

export default function BillingPage() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [plan, setPlan] = useState("monthly");
  const [params, setParams] = useSearchParams();
  const [polling, setPolling] = useState(false);

  const loadStatus = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/billing/status");
      setStatus(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load
  useEffect(() => { loadStatus(); }, [loadStatus]);

  // Poll if we just came back from Stripe with a session_id
  useEffect(() => {
    const sid = params.get("session_id");
    const canceled = params.get("canceled");
    if (canceled) {
      toast.info("Payment was canceled.");
      params.delete("canceled");
      setParams(params, { replace: true });
      return;
    }
    if (!sid) return;
    setPolling(true);
    let attempts = 0;
    const maxAttempts = 8;
    const tick = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/billing/checkout/${sid}`);
        if (data.payment_status === "paid") {
          toast.success(data.subscription ? "Subscription activated — it will renew automatically." : "Payment successful — your subscription has been extended.");
          params.delete("session_id");
          setParams(params, { replace: true });
          setPolling(false);
          loadStatus();
          return;
        }
        if (data.status === "expired") {
          toast.error("Payment session expired. Please try again.");
          setPolling(false);
          return;
        }
      } catch (e) {
        // swallow and keep polling
      }
      if (attempts >= maxAttempts) {
        toast.info("Still processing — refresh in a moment.");
        setPolling(false);
        return;
      }
      setTimeout(tick, 2000);
    };
    tick();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startCheckout = async () => {
    setStarting(true);
    try {
      const { data } = await api.post("/billing/checkout", {
        origin_url: window.location.origin,
        plan,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not start checkout");
      setStarting(false);
    }
  };

  if (loading) {
    return (
      <div className="p-10 text-slate-500" data-testid="billing-loading">Loading…</div>
    );
  }

  const isTrial = status?.status === "trial";
  const isActive = status?.status === "active";
  const isExpired = status?.status === "expired";
  const subActive = !!status?.subscription?.active;
  const refDate = status?.paid_until || status?.trial_ends_at;
  const annualSavings = Math.max(0, (status?.monthly_price_usd || 0) * 12 - (status?.annual_price_usd || 0));

  return (
    <div className="p-6 md:p-10 max-w-4xl" data-testid="billing-page">
      <header className="mb-8">
        <div className="text-xs uppercase tracking-[0.25em] text-slate-500">Subscription</div>
        <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">Billing</h1>
      </header>

      {params.get("locked") === "1" && (
        <div className="mb-6 flex items-start gap-3 rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-900" data-testid="billing-locked-notice">
          <AlertTriangle className="h-5 w-5 shrink-0 text-rose-600" />
          <div>
            <div className="font-semibold">Your plan has expired — payment is required to continue.</div>
            <div className="text-rose-800/90">Sign-in is paused for teachers, officers and staff, and your admin console is limited to this page. Renew below to restore access instantly.</div>
          </div>
        </div>
      )}

      {/* Status card */}
      <section className={`rounded-xl border p-6 mb-6 ${
        isExpired ? "border-destructive/40 bg-destructive/5"
        : isTrial ? "border-amber-200 bg-amber-50/60"
        : "border-emerald-200 bg-emerald-50/60"
      }`} data-testid="billing-status-card">
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] mb-3">
          {isExpired ? (
            <><AlertTriangle className="h-4 w-4 text-destructive" /><span className="text-destructive">Subscription expired</span></>
          ) : isTrial ? (
            <><Sparkles className="h-4 w-4 text-amber-700" /><span className="text-amber-800">Free trial</span></>
          ) : (
            <><CheckCircle2 className="h-4 w-4 text-emerald-700" /><span className="text-emerald-800">Active subscription</span></>
          )}
        </div>
        <div className="font-display text-3xl font-bold tracking-tight text-slate-900">
          {isExpired
            ? "Your access has paused"
            : isTrial
            ? `${status.days_left} day${status.days_left === 1 ? "" : "s"} left in trial`
            : subActive && !status.subscription.cancel_at_period_end
            ? `Auto-renews in ${status.days_left} day${status.days_left === 1 ? "" : "s"}`
            : subActive
            ? `Ends in ${status.days_left} day${status.days_left === 1 ? "" : "s"}`
            : `Renews in ${status.days_left} day${status.days_left === 1 ? "" : "s"}`}
        </div>
        {refDate && (
          <div className="text-sm text-slate-700 mt-1 flex items-center gap-1.5">
            <Calendar className="h-3.5 w-3.5" />
            {isExpired ? "Expired " : isTrial ? "Trial ends " : subActive && !status.subscription.cancel_at_period_end ? "Next charge " : subActive ? "Access until " : "Renews "}
            {new Date(refDate).toLocaleDateString(undefined, { dateStyle: "long" })}
          </div>
        )}
        {isExpired && (
          <div className="mt-3 text-sm text-destructive">
            Your team can no longer sign in, and your console is limited to Billing. Subscribe to restore access.
          </div>
        )}
      </section>

      <SubscriptionManager billing={status} onChanged={loadStatus} />

      {/* Plan card */}
      {!subActive && (
      <section className="rounded-xl border border-slate-200 bg-white p-6" data-testid="billing-plan-card">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <div className="text-xs uppercase tracking-widest text-slate-500 mb-3">Logerd · Pro plan — choose billing period</div>
            <div className="grid sm:grid-cols-2 gap-3" data-testid="billing-plan-selector">
              <button
                type="button"
                onClick={() => setPlan("monthly")}
                data-testid="billing-plan-monthly"
                className={`rounded-xl border-2 p-4 text-left transition ${
                  plan === "monthly" ? "border-primary bg-primary/5" : "border-slate-200 bg-white hover:border-slate-300"
                }`}
              >
                <div className="text-xs uppercase tracking-widest text-slate-500 mb-1">Monthly</div>
                <div className="font-display text-3xl font-bold tracking-tight text-slate-900">
                  ${status?.monthly_price_usd?.toFixed(2)}
                  <span className="text-sm font-normal text-slate-500"> / 30 days</span>
                </div>
              </button>
              <button
                type="button"
                onClick={() => setPlan("annual")}
                data-testid="billing-plan-annual"
                className={`relative rounded-xl border-2 p-4 text-left transition ${
                  plan === "annual" ? "border-primary bg-primary/5" : "border-slate-200 bg-white hover:border-slate-300"
                }`}
              >
                {annualSavings > 0 && (
                  <span className="absolute -top-2.5 right-3 rounded-full bg-emerald-600 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-white">
                    Save ${annualSavings.toFixed(0)}
                  </span>
                )}
                <div className="text-xs uppercase tracking-widest text-slate-500 mb-1">Annual</div>
                <div className="font-display text-3xl font-bold tracking-tight text-slate-900">
                  ${status?.annual_price_usd?.toFixed(2)}
                  <span className="text-sm font-normal text-slate-500"> / 365 days</span>
                </div>
              </button>
            </div>
            <ul className="mt-4 space-y-1.5 text-sm text-slate-700">
              <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-600" /> Unlimited visitors</li>
              <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-600" /> Unlimited officers</li>
              <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-600" /> CSV + PDF exports with your logo</li>
              <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-600" /> Auto-renews every {plan === "annual" ? "year" : "30 days"} · cancel anytime{isActive ? " · first charge when your current paid period ends" : ""}</li>
              {status?.storage_addon?.packs > 0 && (
                <li className="flex items-center gap-2" data-testid="billing-storage-addon">
                  <CheckCircle2 className="h-4 w-4 text-emerald-600" /> Includes storage add-on: {status.storage_addon.packs} pack{status.storage_addon.packs === 1 ? "" : "s"} (+{status.storage_addon.packs * status.storage_addon.gb_per_pack} GB) · +${status.storage_addon.monthly_usd.toFixed(2)}/mo on top of ${status.base_monthly_price_usd.toFixed(2)}
                </li>
              )}
            </ul>
          </div>
          <CreditCard className="h-12 w-12 text-primary/40 shrink-0" />
        </div>
        <button
          onClick={startCheckout}
          disabled={starting || polling}
          data-testid="billing-subscribe-btn"
          className="mt-6 inline-flex items-center justify-center gap-2 rounded-lg bg-primary px-5 py-3 text-sm font-semibold text-white hover:shadow disabled:opacity-60 transition w-full sm:w-auto"
        >
          {(starting || polling) && <Loader2 className="h-4 w-4 animate-spin" />}
          {polling
            ? "Confirming payment…"
            : isExpired
            ? `Subscribe — ${plan === "annual" ? "yearly" : "monthly"} auto-renew`
            : isTrial
            ? `Upgrade — ${plan === "annual" ? "yearly" : "monthly"} auto-renew`
            : `Turn on ${plan === "annual" ? "yearly" : "monthly"} auto-renew`}
        </button>
        <div className="mt-3 text-xs text-slate-500">
          Secure payment by Stripe. You'll be redirected to a hosted checkout page.
        </div>
      </section>
      )}
    </div>
  );
}
